import React from 'react';

// One Question-and-answer card, rendered on BOTH the supplier portal and the
// admin brief page — built once, `side` decides the differences (Q&A card
// add-on brief). Two-speaker layout, one row per message; the green tint on the
// Fusion 2K side is what makes the answer read as the reply rather than more of
// the same.
//
// Input is NORMALISED by the caller (each side has different query field names),
// so this component stays pure presentation and can't drift between the two:
//   question = {
//     id, body, status ('open'|'answered'|'closed_unanswered'),
//     visibility ('private'|'published'), createdAt,
//     askedByMe (bool — supplier side only), askerName, askerOrg,
//     answers: [{ id, body, createdAt, answererName (string|null) }],
//   }
//   side          'supplier' | 'admin'
//   supplierCount (admin) — for "shared with N suppliers" on a published answer
//   composer      (admin, unanswered) — React node placed in the answer slot

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const timeOf = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const fmtFull = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]}, ${timeOf(d)}`;

// First message full; a message under 24h old is relative; a later message on
// the same calendar day as the previous one shows time only — the date is never
// repeated within a thread (§6).
const rowStamp = (iso, prevIso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const ms = Date.now() - d.getTime();
  if (ms >= 0 && ms < 86400000) {
    const h = Math.floor(ms / 3600000);
    if (h >= 1) return `${h} hour${h === 1 ? '' : 's'} ago`;
    const m = Math.max(1, Math.floor(ms / 60000));
    return `${m} min${m === 1 ? '' : 's'} ago`;
  }
  if (prevIso) {
    const p = new Date(prevIso);
    if (!isNaN(p.getTime()) && p.toDateString() === d.toDateString()) return timeOf(d);
  }
  return fmtFull(d);
};

const daysBetween = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

// Initials: first + last (§7). Single-word → one char. Falls back to the org's
// initials upstream when there's no name at all.
const initialsOf = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const BORDER_RADIUS = '3px 12px 12px 12px'; // square corner points at the speaker

// Tile colour is by ROLE, never per person (§7) — so two people with the same
// initials are distinguished by which side they're on + the name beside them.
const TILES = {
  supplier: 'bg-[#EDEFF2] text-[#4C5560]',
  colleague: 'bg-[#7C5CBF]/15 text-[#7C5CBF]',           // a supplier colleague (not this login)
  fusion: 'bg-[#92D400]/20 text-fusion-forest border border-[#3B6D11]/30',
};

function Tile({ initials, tile, ghost }) {
  return (
    <div
      aria-hidden="true"
      className={`flex-shrink-0 w-[34px] h-[34px] rounded-[10px] flex items-center justify-center font-['Metropolis'] text-[11px] font-bold ${ghost ? 'border border-dashed border-[#C7CBBB] text-[#9BA08C] bg-white' : tile}`}>
      {ghost ? '·' : initials}
    </div>
  );
}

function MessageRow({ initials, tile, ghost, name, org, orgGreen, stamp, bubble, big, children }) {
  return (
    <li className="flex items-start gap-3">
      <Tile initials={initials} tile={tile} ghost={ghost} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-['Metropolis'] text-[13px] font-bold text-ink">{name}</span>
          {org && <span className={`font-['Metropolis'] text-[12px] font-semibold ${orgGreen ? 'text-fusion-forest' : 'text-[#51534A]'}`}>{org}</span>}
          {stamp && <span className="font-['Metropolis'] text-[11px] text-[#51534A]">{stamp}</span>}
        </div>
        <div
          className={`mt-1 px-3 py-2 border ${bubble} ${big ? "text-[15px] font-medium" : 'text-sm'}`}
          style={{ borderRadius: BORDER_RADIUS }}>
          {children}
        </div>
      </div>
    </li>
  );
}

export default function QACard({ side, question: q, supplierCount = 0, composer = null }) {
  const isAdmin = side === 'admin';
  const answers = (q.answers || []).slice().sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const isOpen = q.status === 'open';
  const isClosed = q.status === 'closed_unanswered';

  // Question author: "You asked" only on the supplier side when it was this
  // login; a colleague keeps their name + a distinct tile.
  const askerDisplay = (!isAdmin && q.askedByMe) ? 'You asked' : (q.askerName || q.askerOrg || 'Supplier');
  const askerTile = (!isAdmin && q.askedByMe) ? TILES.supplier : (isAdmin ? TILES.supplier : TILES.colleague);
  const askerInitials = initialsOf((!isAdmin && q.askedByMe) ? 'You' : (q.askerName || q.askerOrg));

  // Header-right: Private/Shared always; admin swaps in a waiting badge while open.
  const visPill = (
    <span className={`font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] rounded-md px-2 py-0.5 border whitespace-nowrap ${q.visibility === 'published' ? 'bg-[#92D400]/10 text-fusion-forest border-[#92D400]/40' : 'bg-white text-[#51534A] border-[#DADCCD]'}`}>
      {q.visibility === 'published' ? 'Shared' : 'Private'}
    </span>
  );
  let headerRight = visPill;
  if (isAdmin && isOpen) {
    const d = daysBetween(q.createdAt);
    const cls = d < 1 ? 'text-[#51534A] bg-[#51534A]/10 border-[#DADCCD]'
      : d < 4 ? 'text-[#7A4E10] bg-[#E89A2E]/15 border-[#E89A2E]/40'
        : 'text-[#E0290A] bg-[#E0290A]/15 border-[#E0290A]/40';
    headerRight = (
      <span className={`font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] rounded-md px-2 py-0.5 border whitespace-nowrap ${cls}`}>
        Waiting {d >= 1 ? `${d} day${d === 1 ? '' : 's'}` : 'today'}
      </span>
    );
  }

  return (
    <div className="border border-line rounded-xl bg-white overflow-hidden">
      {/* Header — admin leads with the supplier name; supplier side has none. */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-line bg-[#FBFCF8]">
        {isAdmin ? (
          <span className="font-['Metropolis'] text-sm font-bold text-ink truncate" title={q.askerOrg || 'Supplier'}>{q.askerOrg || 'Supplier'}</span>
        ) : <span aria-hidden="true" />}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {headerRight}
          {isClosed && (
            <span className="font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] rounded-md px-2 py-0.5 border bg-[#51534A]/10 text-[#51534A] border-[#DADCCD] whitespace-nowrap">Closed</span>
          )}
        </div>
      </div>

      <ol className="px-4 py-3 space-y-[11px]">
        {/* Question — supplier speaker */}
        <MessageRow
          initials={askerInitials}
          tile={askerTile}
          name={askerDisplay}
          org={q.askerOrg}
          stamp={rowStamp(q.createdAt, null)}
          bubble="bg-[#FBFCF6] border-[#E9ECDC]">
          <p className="font-['Metropolis'] text-ink whitespace-pre-wrap">{q.body}</p>
        </MessageRow>

        {/* Answers — Fusion 2K speaker, attributed to the individual (§2) */}
        {answers.map((a, i) => {
          const prev = i === 0 ? q.createdAt : answers[i - 1].createdAt;
          const shared = isAdmin && q.visibility === 'published'
            ? ` · shared with ${supplierCount} supplier${supplierCount === 1 ? '' : 's'}` : '';
          return (
            <MessageRow
              key={a.id}
              initials={initialsOf(a.answererName || 'Fusion 2K')}
              tile={TILES.fusion}
              name={a.answererName || 'Fusion 2K'}
              org="Fusion 2K"
              orgGreen
              stamp={`${rowStamp(a.createdAt, prev)}${shared}`}
              bubble="bg-[#F2F6E8] border-[#DCE7CA]"
              big>
              <p className="font-['Metropolis'] text-ink whitespace-pre-wrap">{a.body}</p>
              {a.attachmentUrl && (
                <a href={a.attachmentUrl} target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 font-['Metropolis'] text-[12px] font-semibold text-fusion-forest hover:underline">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                  {a.attachmentName || 'View attachment'}
                </a>
              )}
            </MessageRow>
          );
        })}

        {/* Answer slot when there's no answer yet: the admin composer, or the
            supplier waiting state — same position, same ghost-tile alignment. */}
        {isOpen && answers.length === 0 && (
          isAdmin && composer ? (
            <li className="flex items-start gap-3">
              <Tile ghost />
              <div className="min-w-0 flex-1">{composer}</div>
            </li>
          ) : !isAdmin ? (
            <MessageRow ghost name="Awaiting a reply" stamp={`waiting ${daysBetween(q.createdAt) >= 1 ? `${daysBetween(q.createdAt)} day${daysBetween(q.createdAt) === 1 ? '' : 's'}` : 'today'}`} bubble="bg-white border-[#E9ECDC]">
              <p className="font-['Metropolis'] text-[13px] text-[#51534A] italic">Fusion 2K usually replies within one working day.</p>
            </MessageRow>
          ) : null
        )}

        {isClosed && answers.length === 0 && (
          <MessageRow ghost name="Closed without an answer" bubble="bg-white border-[#E9ECDC]">
            <p className="font-['Metropolis'] text-[13px] text-[#51534A] italic">
              {isAdmin ? 'This was closed without an answer.' : "Sometimes Fusion 2K can't say more at this stage. You can ask a follow-up below."}
            </p>
          </MessageRow>
        )}
      </ol>
    </div>
  );
}
